import { useEffect, useRef, useState } from 'react';
import { Instagram, ChevronLeft, ChevronRight } from 'lucide-react';

declare global {
  interface Window {
    instgrm?: { Embeds?: { process: () => void } };
  }
}

// Raw URLs provided by user
const RAW_URLS: string[] = [
  'https://www.instagram.com/p/DLZ_iI7s8CN',
  'https://www.instagram.com/p/DK9zp1mtR2N',
  'https://www.instagram.com/p/DKMxj5bRxaF',
  'https://www.instagram.com/p/DM75SWPxpFP',
  'https://www.instagram.com/p/DMk2jyRtwnb',
  'https://www.instagram.com/p/DMYS1hJTFT0',
  'https://www.instagram.com/p/DMGCJakxMK2',
  'https://www.instagram.com/p/DL5HfJktH1c',
  'https://www.instagram.com/p/DLsCuseMbSe',
  'https://www.instagram.com/p/DLmBFPPt_3k',
  'https://www.instagram.com/p/DLJi807iwqF',
  'https://www.instagram.com/p/DKz3hCtMpia',
  'https://www.instagram.com/p/DKle3I8ROlc',
  'https://www.instagram.com/p/DKbUF29MV_A',
  'https://www.instagram.com/p/DJ7IOlysEJC',
  'https://www.instagram.com/p/DJL5cUMxrM-',
  'https://www.instagram.com/p/DJEuAUfyaLVpuZh89XAuEAsgVAdwJAXGWeJMco0',
  'https://www.instagram.com/p/DI5paoKMbPo',
  'https://www.instagram.com/p/DIy5h2WtaW8',
  'https://www.instagram.com/p/DIkuj-7xjFl',
  'https://www.instagram.com/p/DIT8r-Zz8ml',
  'https://www.instagram.com/p/DIRuFsCzXIT',
  'https://www.instagram.com/p/DILUkfqxB7B',
  'https://www.instagram.com/p/DH8k8EJTfwT',
  'https://www.instagram.com/p/DHv-kwHMoKA',
  'https://www.instagram.com/p/DHtTSfCMnrp',
  'https://www.instagram.com/p/DHkdlVPxBmR',
  'https://www.instagram.com/p/DHVMutPMr-A',
  'https://www.instagram.com/p/DG8ZnfzTK1W',
  'https://www.instagram.com/p/DG7v67Cxrxg'
];

// Ensure trailing slashes for Instagram oEmbed
const POST_URLS: string[] = RAW_URLS.map((u) => (u.endsWith('/') ? u : `${u}/`));

const SocialWall = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptInitialized, setScriptInitialized] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [failed, setFailed] = useState<boolean[]>(Array(POST_URLS.length).fill(false));
  const [isHovering, setIsHovering] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Observe visibility: load script once when first visible; pause autoplay when not in view
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setIsInView(true);
          if (!scriptInitialized) setScriptInitialized(true);
        } else {
          setIsInView(false);
        }
      },
      { rootMargin: '100px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [scriptInitialized]);

  useEffect(() => {
    if (!scriptInitialized) return;
    const process = () => {
      window.instgrm?.Embeds?.process();
    };

    let script = document.getElementById('instagram-embed-js') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'instagram-embed-js';
      script.async = true;
      script.src = 'https://www.instagram.com/embed.js';
      script.onload = () => {
        setScriptLoaded(true);
        process();
      };
      document.body.appendChild(script);
    } else {
      setScriptLoaded(true);
      process();
    }
  }, [scriptInitialized]);

  // Detect failed embeds (no iframe injected after some time) and hide them
  useEffect(() => {
    if (!scriptInitialized || !scriptLoaded) return;
    let elapsed = 0;
    const interval = setInterval(() => {
      const loadedFlags = POST_URLS.map((_, i) => !!itemRefs.current[i]?.querySelector('iframe'));
      elapsed += 500;
      if (elapsed >= 8000 || loadedFlags.every(Boolean)) {
        setFailed(loadedFlags.map((loaded) => !loaded));
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [scriptInitialized, scriptLoaded]);

  const scrollToIndex = (index: number) => {
    const target = itemRefs.current[index];
    const container = scrollerRef.current;
    if (target && container) {
      container.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      setCurrentIndex(index);
    }
  };

  const getNextIndex = (from: number, step: 1 | -1): number => {
    const len = POST_URLS.length;
    for (let i = 1; i <= len; i++) {
      const idx = (from + step * i + len) % len;
      if (!failed[idx]) return idx;
    }
    return from; // all failed fallback
  };

  // Auto-scroll with hover pause and loop
  useEffect(() => {
    if (!isInView) return; // only autoplay when section visible
    const el = scrollerRef.current;
    if (!el) return;

    const onEnter = () => setIsHovering(true);
    const onLeave = () => setIsHovering(false);
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);

    const id = setInterval(() => {
      if (isHovering) return;
      const next = getNextIndex(currentIndex, 1);
      scrollToIndex(next);
    }, 4500);

    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      clearInterval(id);
    };
  }, [isInView, isHovering, currentIndex, failed]);

  return (
    <section id="testimonials" className="py-10 sm:py-12 bg-white scroll-mt-28 sm:scroll-mt-32 lg:scroll-mt-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={sectionRef}>
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
            <Instagram className="w-6 h-6 mr-2 text-pink-600" /> Follow Our Journey
          </h2>
          <a
            href="https://www.instagram.com/arpan.physiocure/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm sm:text-base text-blue-600 hover:text-blue-700 font-semibold"
          >
            View on Instagram
          </a>
        </div>

        <div className="relative">
          {/* Gradient edges */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white to-transparent" />

          {/* Controls */}
          <div className="absolute -left-3 sm:-left-4 top-1/2 -translate-y-1/2 z-10">
            <button
              onClick={() => scrollToIndex(getNextIndex(currentIndex, -1))}
              className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          </div>
          <div className="absolute -right-3 sm:-right-4 top-1/2 -translate-y-1/2 z-10">
            <button
              onClick={() => scrollToIndex(getNextIndex(currentIndex, 1))}
              className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Horizontal scroller with real embeds */}
          <div
            ref={scrollerRef}
            className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth"
          >
            {POST_URLS.map((url, idx) => (
              failed[idx] ? null : (
                <div
                  key={idx}
                  ref={(el) => (itemRefs.current[idx] = el)}
                  className="snap-start min-w-[290px] sm:min-w-[340px] max-w-[380px] rounded-2xl overflow-hidden border border-gray-100 bg-white shadow-sm relative"
                >
                  {/* Skeleton */}
                  {!scriptLoaded && (
                    <div className="w-full h-[420px] sm:h-[480px] animate-pulse bg-gray-100" />
                  )}
                  <blockquote
                    className="instagram-media"
                    data-instgrm-permalink={`${url}?utm_source=ig_embed&utm_campaign=loading`}
                    data-instgrm-version="14"
                    style={{ background: '#fff', border: 0, margin: 0, padding: 0, width: '100%', minWidth: 0 }}
                  >
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      View this post on Instagram
                    </a>
                  </blockquote>
                  {/* Overlays to hide IG chrome (header/footer, likes) */}
                  <div className="pointer-events-none absolute top-0 left-0 right-0 h-12 sm:h-14 bg-gradient-to-b from-white to-transparent" />
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 sm:h-20 bg-gradient-to-t from-white to-transparent" />
                </div>
              )
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialWall;
